import React from "react";
import {Alert, Button, Form} from "react-bootstrap";
import {CloudDownload} from "react-bootstrap-icons";
import {errorMessage} from "../Alerts/Const";
import {DismissibleAlert} from "../Alerts/DismissibleAlert";
import {USER_EMAIL} from "../Auth/LoginUser";
import {EventType, FrontendModule} from "../CommonApi/Enum";
import {UsageTracking} from "../CommonApi/UsageTracking";
import {ApiCallStatus} from "../CommonFunc/Enum";
import {DataConversionApiClient} from "./DataConversionApiClient";
import {DownloadButton} from "../CommonUIComponent/DownloadButton";
import {CTG_PROTOCOL, DATA_CONVERSION_BUCKET_NAME, dataConversionApiConsts, Z_PRIME_VALUE_THRESHOLD} from "./Const";
import {ConversionResponse, CTGAlphaLISAPutApiRequest} from "./ApiModels";
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { ConsoleLogger } from 'aws-amplify/utils';
import "./DataConversion.css";

interface CTGAlphaLISAInputFormProps {
    protocol: 'ctg' | 'alphalisa';
}

interface CTGAlphaLISAInputFormState {
    inputFile: File | null,
    pico8File: File | null,
    drugExposureDuration: string,
    drugExposureDurationError: string,
    submissionStatus: ApiCallStatus,
    submissionErrorMsg: string,
    conversionId: string | null,
    conversionResponse: ConversionResponse[] | null
}

const logger = new ConsoleLogger("CTGAlphaLISAInputForm.tsx")

const EXAMPLE_FILES: Record<string, { rawData: string; pico8: string }> = {
    ctg: {
        rawData: "examples/ctg_384_raw_data.xlsx",
        pico8: "examples/ctg_384_pico8.xlsx"
    },
    alphalisa: {
        rawData: "examples/alphalisa_384_raw_data.xlsx",
        pico8: "examples/alphalisa_384_pico8.xlsx"
    }
};

export class CTGAlphaLISAInputForm extends React.Component<CTGAlphaLISAInputFormProps, CTGAlphaLISAInputFormState> {
    constructor(props: CTGAlphaLISAInputFormProps) {
        super(props);
        this.state = {
            inputFile: null,
            pico8File: null,
            drugExposureDuration: "",
            drugExposureDurationError: "",
            submissionStatus: ApiCallStatus.NoData,
            submissionErrorMsg: "",
            conversionId: null,
            conversionResponse: null
        }
    }

    setInputFile(e: any) {
        this.setState({
            submissionErrorMsg: "",
            inputFile: e.target.files[0]
        })
    }

    setPico8File(e: any) {
        this.setState({
            submissionErrorMsg: "",
            pico8File: e.target.files[0]
        })
    }

    validateDrugExposureDuration(value: string): string {
        if (!value || value.trim() === "") {
            return "This field is required";
        }
        const pattern = /^\d+(\.\d+)?$/;
        if (!pattern.test(value.trim())) {
            return "Please enter a valid number";
        }
        return "";
    }

    setDrugExposureDuration(e: any) {
        const value = e.target.value;
        const error = this.validateDrugExposureDuration(value);
        this.setState({
            drugExposureDuration: value,
            drugExposureDurationError: error,
            submissionErrorMsg: ""
        })
    }

    getSortedPlates() {
        if (!this.state.conversionResponse) return [];
        
        return this.state.conversionResponse
            .filter((item: any) => item.plateNumber !== null && item.plateNumber !== undefined)
            .sort((a: any, b: any) => parseInt(a.plateNumber) - parseInt(b.plateNumber));
    }

    getZPrimeRowClass(zPrime: string | null): string {
        if (!zPrime || zPrime === 'null') return '';
        
        const zPrimeValue = parseFloat(zPrime);
        if (zPrimeValue < Z_PRIME_VALUE_THRESHOLD) {
            return 'table-danger';
        }
        return '';
    }

    clearForm() {
        // Clear the form inputs
        const inputFileElement = document.getElementById(`${this.props.protocol.toLowerCase()}-input-file-upload`) as HTMLInputElement;
        const pico8FileElement = document.getElementById(`${this.props.protocol.toLowerCase()}-pico8-file-upload`) as HTMLInputElement;
        const drugExposureDurationElement = document.getElementById(`${this.props.protocol.toLowerCase()}-drug-exposure-duration`) as HTMLInputElement;
        
        if (inputFileElement) inputFileElement.value = "";
        if (pico8FileElement) pico8FileElement.value = "";
        if (drugExposureDurationElement) drugExposureDurationElement.value = "";
        
        // Reset state to initial values
        this.setState({
            inputFile: null,
            pico8File: null,
            drugExposureDuration: "",
            drugExposureDurationError: "",
            submissionStatus: ApiCallStatus.NoData,
            submissionErrorMsg: "",
            conversionId: null,
            conversionResponse: null
        });
    }


    submitPipeline(e: any) {
        e.preventDefault()
        
        if (!this.state.inputFile || !this.state.pico8File) {
            this.setState({
                submissionErrorMsg: "Please select both input file and PICO8 file",
                submissionStatus: ApiCallStatus.Error
            })
            return
        }

        const durationError = this.validateDrugExposureDuration(this.state.drugExposureDuration);
        if (durationError) {
            this.setState({
                drugExposureDurationError: durationError,
                submissionStatus: ApiCallStatus.Error,
                submissionErrorMsg: durationError
            })
            return
        }

        const reportID: string = uuidv4();

        this.setState({
            submissionStatus: ApiCallStatus.Loading,
            submissionErrorMsg: ""
        }, async () => {
            try {
                // Upload input file to S3 first
                const inputFileName = this.state.inputFile!.name;
                const inputS3Key = `public/${reportID}/${inputFileName}`;
                
                logger.info("Starting input file upload...");
                const inputUploadResult = await uploadData({
                    path: inputS3Key,
                    data: this.state.inputFile!,
                    options: {
                        bucket: DATA_CONVERSION_BUCKET_NAME
                    }
                }).result;
                logger.info("Input file upload completed:", inputUploadResult);

                // Upload PICO8 file to S3
                const pico8FileName = this.state.pico8File!.name;
                const pico8S3Key = `public/${reportID}/${pico8FileName}`;
                
                logger.info("Starting PICO8 file upload...");
                const pico8UploadResult = await uploadData({
                    path: pico8S3Key,
                    data: this.state.pico8File!,
                    options: {
                        bucket: DATA_CONVERSION_BUCKET_NAME
                    }
                }).result;
                logger.info("PICO8 file upload completed:", pico8UploadResult);

                UsageTracking.recordEvent({
                    uuid: uuidv4(),
                    module: FrontendModule.DataConversion,
                    event: EventType.Import,
                    table: dataConversionApiConsts.TABLE_NAME,
                    database: dataConversionApiConsts.DATABASE_NAME,
                    extraMetadata: {
                        "conversionMethod": this.props.protocol === CTG_PROTOCOL ? 'CTG' : 'AlphaLISA',
                        "owner": USER_EMAIL,
                        "conversionRequestUuid": reportID,
                        "inputFileName": inputFileName,
                        "pico8FileName": pico8FileName,
                    }
                })

                // Now call the API with S3 paths after both uploads are complete
                const request: CTGAlphaLISAPutApiRequest = {
                    conversionRequestUuid: reportID,
                    inputFileS3Path: inputS3Key,
                    pico8FileS3Path: pico8S3Key,
                    protocol: this.props.protocol,
                    drugExposureDuration: this.state.drugExposureDuration.trim(),
                    userEmail: USER_EMAIL
                };
                
                logger.info("Starting API call with S3 paths:", request);
                const response = await DataConversionApiClient.convertCTGAlphaLISA(request);
                logger.info(`${this.props.protocol === CTG_PROTOCOL ? 'CTG' : 'AlphaLISA'} conversion response:`, response);
                
                this.setState({
                    submissionStatus: ApiCallStatus.Completed,
                    conversionId: response.data[0]?.requestId || reportID,
                    conversionResponse: response.data
                });
            } catch (err: any) {
                logger.error("Error during upload or conversion:", err);
                this.setState({
                    submissionStatus: ApiCallStatus.Error,
                    submissionErrorMsg: errorMessage(err)
                });
            }
        })
    }

    render() {
        const protocolName = this.props.protocol === CTG_PROTOCOL ? 'CTG' : 'AlphaLISA';

        return (
            <>
                <h4 className="protocol-title">{protocolName}-96/384 Well Plate Conversion</h4>
                <Alert className="info-subtle">Normalizes and converts {protocolName}-96/384 well plate data for standard CDD/PRISM import. Requires two input files, <strong>raw data</strong> and <strong>PICO8 report.</strong></Alert>
                
                <div className="row">
                    <div className="col-md-6">
                        <Form onSubmit={this.submitPipeline.bind(this)}>
                            <Form.Group className="mb-3">
                                <Alert className="info-subtle">
                                    <div>
                                        Files must follow the required format - download examples:
                                        <ul>
                                            <li className="mb-2">
                                                <DownloadButton 
                                                    buttonSize={"sm"} 
                                                    s3Bucket={DATA_CONVERSION_BUCKET_NAME} 
                                                    s3Keys={[EXAMPLE_FILES[this.props.protocol].rawData]} 
                                                    variant={"success"} 
                                                    text={"Raw Data Example"}
                                                    icon={<CloudDownload className="me-2" />}
                                                />
                                            </li>
                                            <li>
                                                <DownloadButton 
                                                    buttonSize={"sm"} 
                                                    s3Bucket={DATA_CONVERSION_BUCKET_NAME} 
                                                    s3Keys={[EXAMPLE_FILES[this.props.protocol].pico8]} 
                                                    variant={"success"} 
                                                    text={"PICO8 Report Example"}
                                                    icon={<CloudDownload className="me-2" />}
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                </Alert>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Upload {protocolName} Raw Data File (Excel)</Form.Label>
                                <Form.Control
                                    id={`${this.props.protocol.toLowerCase()}-input-file-upload`}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                    onChange={this.setInputFile.bind(this)}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Upload PICO8 Report File (Excel)</Form.Label>
                                <Form.Control
                                    id={`${this.props.protocol.toLowerCase()}-pico8-file-upload`}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                    onChange={this.setPico8File.bind(this)}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{this.props.protocol === CTG_PROTOCOL ? 'Drug Exposure Duration (in days)' : 'Drug Exposure Duration (in hours)'}</Form.Label>
                                <Form.Control
                                    id={`${this.props.protocol.toLowerCase()}-drug-exposure-duration`}
                                    type="text"
                                    placeholder="e.g., 2, 47"
                                    value={this.state.drugExposureDuration}
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                    onChange={this.setDrugExposureDuration.bind(this)}
                                    isInvalid={!!this.state.drugExposureDurationError}
                                    required
                                />
                                {this.state.drugExposureDurationError && (
                                    <Form.Control.Feedback type="invalid">
                                        {this.state.drugExposureDurationError}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                >
                                    {this.state.submissionStatus === ApiCallStatus.Loading ? "Converting ..." : "Convert"}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                    onClick={this.clearForm.bind(this)}
                                >
                                    Clear
                                </Button>
                            </div>
                        </Form>
                        {this.state.submissionStatus === ApiCallStatus.Error && <DismissibleAlert className={"mt-2"} message={this.state.submissionErrorMsg} />}
                    </div>
                    
                    <div className="col-md-6">
                        {this.state.submissionStatus === ApiCallStatus.Completed && (
                            <Alert variant={"success"}>
                                <Alert.Heading>Conversion Completed!</Alert.Heading>
                                
                                {this.getSortedPlates().length > 0 && (
                                    <div className="mb-3">
                                        <p className="text-muted small mb-2">Note: Z-prime values below <span className="text-danger">{Z_PRIME_VALUE_THRESHOLD}</span> should be interpreted with caution</p>
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Plate Number</th>
                                                    <th>Z-Prime Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.getSortedPlates().map((item: any, index: number) => (
                                                    <tr key={index} className={this.getZPrimeRowClass(item.zPrime)}>
                                                        <td>{item.plateNumber}</td>
                                                        <td>{item.zPrime && item.zPrime !== 'null' ? parseFloat(item.zPrime).toFixed(5) : 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {this.state.conversionResponse && this.state.conversionResponse.length > 0 && (
                                    <div className="mb-3">
                                        <DownloadButton 
                                            buttonSize="sm" 
                                            s3Bucket={this.state.conversionResponse[0].outputZipFileS3Bucket || ''} 
                                            s3Keys={[this.state.conversionResponse[0].outputZipFileS3Key || '']} 
                                            variant="success" 
                                            text="CDD converted files"
                                            icon={<CloudDownload className="me-2" />}
                                        />
                                    </div>
                                )}
                            </Alert>
                        )}
                        {this.state.submissionStatus === ApiCallStatus.Loading && (
                            <Alert variant={"info"}>
                                <Alert.Heading>Conversion in progress...</Alert.Heading>
                                <p>Please wait while we convert your files...</p>
                            </Alert>
                        )}
                    </div>
                </div>
            </>
        );
    }
}
